// Stripe Checkout entry point for project sponsorships.
//
// Three modes, chosen at runtime based on env vars:
//   1. Payment Link mode — if REACT_APP_STRIPE_LINK_{COFFEE,MOCKTAIL} is set,
//      redirect to the pre-created Stripe Payment Link. No backend needed.
//      `client_reference_id` carries the projectId so the webhook can attribute
//      the sponsorship.
//   2. Publishable key mode — if a publishable key exists but no link,
//      post to /api/create-checkout-session (backend TODO) and redirect
//      via @stripe/stripe-js.
//   3. Mock mode — no Stripe config. Call recordSponsorship directly and
//      show a success toast. Useful for local dev / first demo.
import { loadStripe } from '@stripe/stripe-js';
import {
    STRIPE_PUBLISHABLE_KEY,
    STRIPE_PAYMENT_LINK_COFFEE,
    STRIPE_PAYMENT_LINK_MOCKTAIL,
    SPONSOR_AMOUNT_TWD,
} from '../data/sponsor-constants';
import { recordSponsorship } from './sponsorship';

let stripePromise = null;
const getStripe = () => {
    if (!STRIPE_PUBLISHABLE_KEY) return null;
    if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
    return stripePromise;
};

const paymentLinkFor = (type) =>
    type === 'coffee' ? STRIPE_PAYMENT_LINK_COFFEE : STRIPE_PAYMENT_LINK_MOCKTAIL;

// Build the success URL our ProjectsPage watches for redirect attribution.
const buildSuccessUrl = (projectId, type) => {
    const base = `${window.location.origin}/projects`;
    const qs = `sponsor=success&project=${encodeURIComponent(projectId)}&type=${encodeURIComponent(type)}`;
    return `${base}?${qs}`;
};

export const startCheckout = async ({ projectId, type }) => {
    const amount = SPONSOR_AMOUNT_TWD[type] || 0;
    const link = paymentLinkFor(type);

    if (link) {
        const url = new URL(link);
        url.searchParams.set('client_reference_id', projectId);
        // Stripe Payment Links support success_url override when enabled in dashboard.
        url.searchParams.set('redirect_url', buildSuccessUrl(projectId, type));
        window.location.href = url.toString();
        return { mode: 'payment_link' };
    }

    const stripe = await getStripe();
    if (stripe) {
        // Backend must expose POST /api/create-checkout-session → { id }.
        // Until that endpoint ships we fall through to mock mode.
        try {
            const resp = await fetch('/api/create-checkout-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    projectId,
                    type,
                    amount,
                    currency: 'twd',
                    successUrl: buildSuccessUrl(projectId, type),
                    cancelUrl: `${window.location.origin}/projects`,
                }),
            });
            if (resp.ok) {
                const { id } = await resp.json();
                const { error } = await stripe.redirectToCheckout({ sessionId: id });
                if (error) throw error;
                return { mode: 'stripe_session' };
            }
        } catch (err) {
            console.warn('Stripe backend unavailable, falling back to mock:', err);
        }
    }

    // Mock path — attribute the sponsorship client-side and return.
    await recordSponsorship(projectId, type);
    return { mode: 'mock', amount };
};
